import { copyJournalLink } from './journal-share-utils.mjs';

export const initJournal = (article) => {
    if (article.dataset.journalReady === 'true') return;
    article.dataset.journalReady = 'true';

    const content = article.querySelector('[data-journal-content]');
    const progress = article.querySelector('[data-reading-progress]');
    const toc = article.querySelector('[data-journal-toc]');
    const links = [...(toc?.querySelectorAll('nav a') || [])];
    const sections = links.map((link) => ({
        link,
        heading: document.getElementById(link.getAttribute('href').slice(1)),
    })).filter(({ heading }) => heading);
    const desktop = window.matchMedia('(min-width: 1024px)');
    const syncToc = () => {
        if (toc) toc.open = desktop.matches;
    };

    syncToc();
    desktop.addEventListener('change', syncToc);

    let queued = false;
    let current = null;
    const update = () => {
        queued = false;
        if (!content) return;
        const bounds = content.getBoundingClientRect();
        const distance = Math.max(1, bounds.height - window.innerHeight + 80);
        const percentage = Math.min(1, Math.max(0, (80 - bounds.top) / distance));
        if (progress) progress.style.transform = `scaleX(${percentage})`;

        let active = null;
        for (const section of sections) {
            if (section.heading.getBoundingClientRect().top <= 160) active = section.link;
        }
        if (active === current) return;
        current?.removeAttribute('aria-current');
        active?.setAttribute('aria-current', 'location');
        current = active;
    };
    const scheduleUpdate = () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    if (content && 'ResizeObserver' in window) {
        new ResizeObserver(scheduleUpdate).observe(content);
    }
    update();

    const share = article.querySelector('[data-journal-share]');
    if (!share) return;
    const trigger = share.querySelector('summary');
    const input = share.querySelector('[data-share-url]');
    const copy = share.querySelector('[data-share-copy]');
    const copyLabel = copy?.querySelector('[data-copy-label]');
    const native = share.querySelector('[data-share-native]');
    const status = share.querySelector('[data-share-status]');
    if (!input || !copy || !copyLabel || !status) return;
    const copyText = copyLabel.textContent;
    copy.hidden = false;

    share.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !share.open) return;
        event.preventDefault();
        share.open = false;
        trigger.focus({ preventScroll: true });
    });
    share.addEventListener('toggle', () => {
        if (share.open) return;
        status.textContent = '';
        copy.removeAttribute('data-copied');
        copyLabel.textContent = copyText;
    });
    input.addEventListener('focus', () => input.select());
    input.addEventListener('click', () => input.select());

    copy.addEventListener('click', async () => {
        if (copy.disabled) return;
        copy.disabled = true;
        copy.setAttribute('aria-busy', 'true');
        copy.removeAttribute('data-copied');
        copyLabel.textContent = 'Kopiowanie…';
        status.textContent = '';
        const copied = await copyJournalLink(input);
        copy.disabled = false;
        copy.removeAttribute('aria-busy');
        copyLabel.textContent = copied && share.open ? 'Skopiowano' : copyText;
        if (!share.open) return;
        if (copied) {
            copy.setAttribute('data-copied', '');
            status.textContent = 'Link skopiowany. Możesz go teraz wkleić w wiadomości.';
            if (document.activeElement === input) copy.focus({ preventScroll: true });
        } else {
            status.textContent = 'Zaznaczyliśmy link. Wybierz „Kopiuj” lub naciśnij Ctrl+C (⌘C na Macu).';
        }
    });

    const shareData = { url: input.value, title: share.dataset.title };
    if (!native || typeof navigator.share !== 'function') return;
    if (navigator.canShare && !navigator.canShare(shareData)) return;
    native.hidden = false;
    native.addEventListener('click', async () => {
        if (native.disabled) return;
        native.disabled = true;
        native.setAttribute('aria-busy', 'true');
        status.textContent = '';
        try {
            await navigator.share(shareData);
        } catch (error) {
            if (error.name !== 'AbortError') {
                status.textContent = 'Wybierz jedną z opcji powyżej lub skopiuj link.';
            }
        } finally {
            native.disabled = false;
            native.removeAttribute('aria-busy');
        }
    });
};
